// Fichier: /pages/NotFound.tsx (ou équivalent)
// Version adaptée pour SIS, avec la structure et la logique conservées.

import React from 'react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Link } from 'react-router-dom';
// Icônes adaptées pour SIS
import { Home, Wrench } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { motion } from 'framer-motion';
import { useTenantContext } from '../../context/TenantContext';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const NotFound: React.FC = () => {
  const { tenant, previewConfig } = useTenantContext();

  // Thème NotFound (preview > tenant)
  const notFoundTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? {} as any;
    const themeColors = {
      c1: tenant?.config?.color1 || '#fe8d17', // primaire (bouton)
      c2: tenant?.config?.color2 || '#008aef', // secondaire (bouton)
      c3: tenant?.config?.color3 || '#ffffff',
      c4: tenant?.config?.color4 || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'Wrench',  color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Server',  color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Code',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'HardDrive', color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);

  // Configuration SEO personnalisée par tenant
  const seoConfig = React.useMemo(() => {
    const seo = tenant?.config?.seo || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `404 - Page Introuvable | ${tenant?.name || 'Notre site'}`,
      description: seo.description || `Cette page semble avoir une erreur de connexion. Retournez à l'accueil pour découvrir ${tenant?.name || 'nos services'}.`,
      image: seo.ogImage || tenant?.logo || tenant?.icon,
      url: `https://${currentDomain}/404`
    };
  }, [tenant]);

  return (
    <PublicLayout>
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        image={seoConfig.image}
        url={seoConfig.url}
      />

      <div className="min-h-screen relative overflow-hidden">
        {/* Fond d'écran dynamique du tenant avec thème SIS */}
        <div
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/1779487/pexels-photo-1779487.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
            backgroundAttachment: 'fixed'
          }}
        >
          {notFoundTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: notFoundTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[notFoundTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(notFoundTheme.overlay.color1, notFoundTheme.overlay.opacity1)},
                    ${hexToRgba(notFoundTheme.overlay.color2, notFoundTheme.overlay.opacity2)},
                    ${hexToRgba(notFoundTheme.overlay.color3, notFoundTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !notFoundTheme.overlay.useGradient
                ? hexToRgba(notFoundTheme.overlay.color1, notFoundTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes SIS */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {notFoundTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(notFoundTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[notFoundTheme.icons[0].icon] || iconMap.Wrench;
                return IconComponent ? <IconComponent size={notFoundTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {notFoundTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(notFoundTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[notFoundTheme.icons[1].icon] || iconMap.Server;
                return IconComponent ? <IconComponent size={notFoundTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {notFoundTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(notFoundTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[notFoundTheme.icons[2].icon] || iconMap.Code;
                return IconComponent ? <IconComponent size={notFoundTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {notFoundTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(notFoundTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[notFoundTheme.icons[3].icon] || iconMap.HardDrive;
                return IconComponent ? <IconComponent size={notFoundTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>

        {/* Contenu centré avec thème SIS */}
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-8xl md:text-9xl font-bold mb-4 tracking-wider" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>404</h1>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Page Introuvable</h2>
              <p className="text-lg mb-8 max-w-md mx-auto" style={{ color: `${tenant?.config?.color3 || '#ffffff'}cc` }}>
                Il semblerait que cette page rencontre une erreur de connexion, ou n'existe tout simplement plus.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                style={{ 
                  background: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                  color: tenant?.config?.color3 || '#ffffff'
                }}
              >
                <Home className="w-5 h-5" />
                Retour à l'accueil
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-full transition-all duration-300"
                style={{ 
                  backgroundColor: `${tenant?.config?.color3 || '#ffffff'}1a`,
                  borderColor: `${tenant?.config?.color2 || '#008aef'}33`,
                  color: tenant?.config?.color3 || '#ffffff',
                  border: `1px solid ${tenant?.config?.color2 || '#008aef'}33`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${tenant?.config?.color3 || '#ffffff'}33`;
                  e.currentTarget.style.borderColor = `${tenant?.config?.color2 || '#008aef'}66`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${tenant?.config?.color3 || '#ffffff'}1a`;
                  e.currentTarget.style.borderColor = `${tenant?.config?.color2 || '#008aef'}33`;
                }}
              >
                <Wrench className="w-5 h-5" />
                Nous contacter
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default NotFound;